import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import Modal from '../../../../components/Modals/Modal';
import ApiDataViewer from './ApiDataViewer';
import { useApiDatas } from '../../../../services/apiDatas.service';
import Pagination from '../../../../components/Table/Pagination';
import { LoaderComponent } from '../../../../components/Table/styles';
import { FiLayers, FiFileText, FiCheckSquare, FiCreditCard, FiActivity } from 'react-icons/fi';

export interface ApiDataModalHandle {
  openModal: (integrationId: string, integrationName: string) => void;
}

const TabsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  overflow-x: auto;
  flex-shrink: 0;
`;

interface TabButtonProps {
  active: boolean;
  color?: string;
}

const TabButton = styled.button<TabButtonProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: 1px solid ${({ active, color }) => (active ? color || '#2563eb' : '#cbd5e1')};
  background-color: ${({ active, color }) => (active ? color || '#2563eb' : '#ffffff')};
  color: ${({ active }) => (active ? '#ffffff' : '#334155')};

  &:hover {
    border-color: ${({ color }) => color || '#2563eb'};
    background-color: ${({ active, color }) => (active ? color || '#2563eb' : '#f1f5f9')};
  }

  svg {
    font-size: 15px;
  }
`;

const Badge = styled.span<{ active: boolean }>`
  font-size: 11px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 12px;
  background-color: ${({ active }) => (active ? 'rgba(255, 255, 255, 0.28)' : '#e2e8f0')};
  color: ${({ active }) => (active ? '#ffffff' : '#475569')};
`;

const ContentContainer = styled.div`
  padding: 16px 20px;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #f8fafc;
  box-sizing: border-box;
  overflow: hidden;
`;

const TableWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
`;

const HeaderInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 4px 12px 4px;
  font-size: 13px;
  color: #64748b;
  font-weight: 500;

  strong {
    color: #0f172a;
    font-weight: 700;
  }
`;

const ApiDataModal = forwardRef<ApiDataModalHandle, {}>((props, ref) => {
  const modalRef = useRef<any>(null);
  const [integrationId, setIntegrationId] = useState<string | null>(null);
  const [integrationName, setIntegrationName] = useState<string>('');
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'ALL' | 'LIC' | 'NE' | 'NP' | 'XD'>('LIC');

  const { data: apiData, isLoading } = useApiDatas(integrationId, page, activeTab);

  useImperativeHandle(ref, () => ({
    openModal(id: string, name: string) {
      setIntegrationId(id);
      setIntegrationName(name);
      setPage(1);
      setActiveTab('LIC'); // Inicia em Licitações por padrão
      modalRef.current?.openModal();
    },
  }));

  const summary = apiData?.summary || {};
  const countAll = summary.ALL || apiData?.total || 0;
  const countLic = summary.LIC ?? 0;
  const countNe = summary.NE ?? 0;
  const countNp = summary.NP ?? 0;
  const countXd = summary.XD ?? 0;

  // Auto-selecionar aba que possui registros caso a aba atual esteja zerada
  React.useEffect(() => {
    if (!apiData?.summary) return;
    const s = apiData.summary;
    const currentCount =
      activeTab === 'LIC' ? s.LIC :
      activeTab === 'NE' ? s.NE :
      activeTab === 'NP' ? s.NP :
      activeTab === 'XD' ? s.XD :
      s.ALL;

    if ((currentCount === 0 || currentCount === undefined) && (s.ALL || 0) > 0) {
      if ((s.NP || 0) > 0) setActiveTab('NP');
      else if ((s.LIC || 0) > 0) setActiveTab('LIC');
      else if ((s.NE || 0) > 0) setActiveTab('NE');
      else if ((s.XD || 0) > 0) setActiveTab('XD');
      else setActiveTab('ALL');
    }
  }, [apiData?.summary]);

  const handleTabChange = (tab: 'ALL' | 'LIC' | 'NE' | 'NP' | 'XD') => {
    setActiveTab(tab);
    setPage(1);
  };

  const records = apiData?.data?.map((d: any) => {
    const raw = d.data || {};
    const inferredTipo = raw.tipoRegistro || (
      raw.numero_documento_caixa || raw.Doc_Caixa ? 'NP' :
      raw.descricao_objeto_licitacao || raw.modalidade_licitacao || raw.N_Proc ? 'LIC' :
      raw.valor_empenhado || raw.N_Empenho ? 'NE' :
      raw.numero_documento_despesa_extra ? 'XD' : 'OUTRO'
    );
    return {
      ...raw,
      _id: d._id,
      period: d.period,
      tipoRegistro: inferredTipo
    };
  }) || [];

  return (
    <Modal title={`Dados Importados: ${integrationName}`} ref={modalRef} isLarge>
      <div style={{ width: '100%', height: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TabsBar>
          <TabButton
            active={activeTab === 'LIC'}
            color="#0284c7"
            onClick={() => handleTabChange('LIC')}
            type="button"
          >
            <FiFileText />
            <span>Licitações e Contratos</span>
            <Badge active={activeTab === 'LIC'}>{countLic}</Badge>
          </TabButton>

          <TabButton
            active={activeTab === 'NE'}
            color="#059669"
            onClick={() => handleTabChange('NE')}
            type="button"
          >
            <FiCheckSquare />
            <span>Notas de Empenho</span>
            <Badge active={activeTab === 'NE'}>{countNe}</Badge>
          </TabButton>

          <TabButton
            active={activeTab === 'NP'}
            color="#d97706"
            onClick={() => handleTabChange('NP')}
            type="button"
          >
            <FiCreditCard />
            <span>Notas de Pagamento / Caixa</span>
            <Badge active={activeTab === 'NP'}>{countNp}</Badge>
          </TabButton>

          {countXd > 0 && (
            <TabButton
              active={activeTab === 'XD'}
              color="#7c3aed"
              onClick={() => handleTabChange('XD')}
              type="button"
            >
              <FiActivity />
              <span>Despesas Extras</span>
              <Badge active={activeTab === 'XD'}>{countXd}</Badge>
            </TabButton>
          )}

          <TabButton
            active={activeTab === 'ALL'}
            color="#475569"
            onClick={() => handleTabChange('ALL')}
            type="button"
          >
            <FiLayers />
            <span>Todos os Dados</span>
            <Badge active={activeTab === 'ALL'}>{countAll}</Badge>
          </TabButton>
        </TabsBar>

        <ContentContainer>
          <HeaderInfo>
            <span>
              Exibindo registros da categoria{' '}
              <strong>
                {activeTab === 'LIC' && 'Licitações e Contratos'}
                {activeTab === 'NE' && 'Notas de Empenho'}
                {activeTab === 'NP' && 'Notas de Pagamento (Doc. Caixa)'}
                {activeTab === 'XD' && 'Despesas Extraorçamentárias'}
                {activeTab === 'ALL' && 'Geral (Todos os Tipos)'}
              </strong>
            </span>
            <span>
              Total: <strong>{apiData?.total || 0}</strong> registros encontrados
            </span>
          </HeaderInfo>

          <TableWrapper>
            {isLoading ? (
              <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
                <LoaderComponent />
              </div>
            ) : (
              <ApiDataViewer data={records} activeTab={activeTab} />
            )}
          </TableWrapper>

          {apiData && apiData.totalPages > 1 && (
            <div style={{ marginTop: 12 }}>
              <Pagination
                totalPages={apiData.totalPages}
                page={page}
                perPage={apiData.limit}
                pageChangeHandler={setPage}
              />
            </div>
          )}
        </ContentContainer>
      </div>
    </Modal>
  );
});

export default ApiDataModal;
